export type OrderSubmissionCheckPreferences =
	Array<OrderSubmissionCheckPreference>;

export interface OrderSubmissionCheckPreference {
	type: OrderSubmissionCheckType;
	crm: OrderSubmissionCheckState;
	ecommerce: OrderSubmissionCheckState;
	// PD-14234: roles (rbac_role ids) additionally allowed to approve/reject
	// orders held by THIS check on the given channel. Only meaningful when
	// the channel state is 'hold' or 'holdWarning'. Users holding the
	// blanket orders:approve / orders:reject actions are always allowed and
	// don't need to be listed here.
	crmApproverRoleIds?: Array<number>;
	ecommerceApproverRoleIds?: Array<number>;
}

export interface MinimumOrderPriceCheckPreference
	extends OrderSubmissionCheckPreference {
	crmMinimum: number;
	ecommerceMinimum: number;
}

export interface MaximumDiscountCheckPreference
	extends OrderSubmissionCheckPreference {
	// New CRM-specific fields
	crmMaximumDiscount: number;
	crmDiscountType: 'percentage' | 'amount';

	// New Ecommerce-specific fields
	ecommerceMaximumDiscount: number;
	ecommerceDiscountType: 'percentage' | 'amount';

	// Legacy fields - used by legacy ecommerce (mirrors ecommerce values)
	// TODO: Remove when legacy ecommerce is replaced
	maximumDiscount: number;
	discountType: 'percentage' | 'amount';
}

export interface ControlledSubstanceCheckPreference
	extends OrderSubmissionCheckPreference {
	// New CRM-specific fields
	crmMaximumControlPercent: number;
	crmUnitType: 'unitsPurchased' | 'dollarAmount' | 'sizeCountQuantity';

	// New Ecommerce-specific fields
	ecommerceMaximumControlPercent: number;
	ecommerceUnitType: 'unitsPurchased' | 'dollarAmount' | 'sizeCountQuantity';

	// Legacy fields - used by legacy ecommerce (mirrors ecommerce values)
	// TODO: Remove when legacy ecommerce is replaced
	maximumControlPercent: number | string;
	unitType: 'unitsPurchased' | 'dollarAmount' | 'sizeCountQuantity';
}

export type PriceBelowCostCostField =
	| 'sellCost'
	| 'acquisitionCost'
	| 'averageCost';

export interface PriceBelowCostCheckPreference
	extends OrderSubmissionCheckPreference {
	crmCostField: PriceBelowCostCostField;
	ecommerceCostField: PriceBelowCostCostField;
}

export type OrderSubmissionCheckType =
	| 'creditLimit'
	| 'creditHold'
	| 'dailyItemLimit'
	| 'monthlyItemLimit'
	| 'perOrderLimit'
	| 'minimumOrderPrice'
	| 'minimumPrice'
	| 'priceBelowCost'
	| 'outOfStock'
	| 'shortDate'
	| 'maximumDiscount'
	| 'controlPercentage'
	| 'stateBoardLicense'
	| 'deaLicense';

export type OrderSubmissionCheckState =
	| 'off'
	| 'warning'
	| 'error'
	| 'hold'
	| 'holdWarning';

export interface OrderEntryFeaturePreference {
	isEnabled?: boolean;
	credit?: OrderEntryCreditPreference;
	crm?: {
		shippingDisclaimer?: string | null;
		shippingOptions?: Array<string>;
		fulfillmentOptions?: Array<string>;
	};
	ecomm?: {
		isEnabled?: boolean;
		shippingDisclaimer?: string | null;
		shippingOptions?: Array<string>;
	};
}

export interface OrderEntryCreditPreference {
	ignoreCreditLimit?: boolean;
	ignoreCreditHold?: boolean;
	ignorePaymentTerms?: Array<string>;
}

export enum ItemDeaScheduleLicenseLevelEnum {
	FEDERAL = 'Federal',
	STATE = 'State',
}

export type ItemDeaType =
	| 'OTC'
	| 'RX'
	| 'C1'
	| 'C2'
	| 'C2N'
	| 'C3'
	| 'C3N'
	| 'C4'
	| 'C5';

/**
 * Ecommerce feature preference — controls anonymous browsing behavior,
 * company restriction levels, and ecommerce order credit settings.
 * Stored as 'instance-feature-ecommerce' preference.
 */
export interface EcommerceFeaturePreference {
	anonymousBrowsing?: {
		enabled?: boolean;
		settings?: {
			showQuantity?: boolean;
			showPricing?: boolean;
		};
	};
	company?: {
		restrictionLevel?: string;
	};
	orders?: {
		credit?: OrderEntryCreditPreference;
	};
	/** Portal catalog configuration — controls which filters and sort options are available */
	catalog?: {
		/** Enabled filter keys (e.g. 'in_stock', 'short_date', 'previously_purchased', 'on_order'). Defaults to ['in_stock'] when not set. */
		filters?: Array<string>;
		/** Enabled sort option keys: 'relevance', 'popularity', 'recently_added' (internal createdDate), 'newest' (external createdDate), 'name', 'price'. Defaults to all of these when not set. */
		sortOptions?: Array<string>;
	};
}

export interface OrderValidationResult {
	hasErrors: boolean;
	hasWarnings: boolean;
	requiresApproval: boolean;
	errors: Array<ValidationIssue>;
	warnings: Array<ValidationIssue>;
	approvalReasons: Array<ApprovalRequiredReason>;
}

export interface ValidationIssue {
	type: ValidationIssueType;
	itemId?: number;
	message: string;
	severity: 'error' | 'warning' | 'hold';
	data?: unknown;
}

export type ValidationIssueType =
	| 'creditLimit'
	| 'creditHold'
	| 'perOrderLimit'
	| 'dailyLimit'
	| 'dailyItemLimit'
	| 'monthlyLimit'
	| 'monthlyItemLimit'
	| 'minimumPrice'
	| 'priceBelowCost'
	| 'minimumOrder'
	| 'minimumOrderPrice'
	| 'maximumDiscount'
	| 'licenseExpired'
	| 'licenseInvalid'
	| 'outOfStock'
	| 'shortDate'
	| 'missingBillingAddress'
	| 'missingShippingAddress'
	| 'paymentFailed'
	| 'promoChanged'
	| 'stateBoardLicenseInvalid'
	| 'deaLicenseInvalid'
	| 'fullPalletOnly'
	| 'itemInactive'
	| 'itemNotAccessible'
	// A product-level item (a non-sellable grouping row for variants) reached a
	// transactional line. See docs/claude/product-variants/README.md.
	| 'itemProductLevel';

export interface ItemFeaturePreference {
	maxPriceDecimalPrecision?: number;
	/**
	 * Legacy `instance-feature-item` key (PD-16173). When true, an item with no
	 * minimum price of its own is held to the price the shop shows for the
	 * client (contract / saved price, else list price). The preference set can
	 * be role-scoped, so read it through
	 * PreferencesService.isDefaultMinimumPriceEnabled rather than the tenant
	 * default set.
	 */
	defaultMinimumPrice?: boolean;
}

export enum ApprovalRequiredReason {
	CREDIT_HOLD = 'CREDIT_HOLD',
	CREDIT_LIMIT = 'CREDIT_LIMIT',
	CONTROLLED_SUBSTANCE_LIMIT = 'CONTROLLED_SUBSTANCE_LIMIT',
	MAXIMUM_DISCOUNT = 'MAXIMUM_DISCOUNT',
	MINIMUM_ORDER_PRICE = 'MINIMUM_ORDER_PRICE',
	OUT_OF_STOCK = 'OUT_OF_STOCK',
	SHORT_DATE = 'SHORT_DATE',
	PRICE_BELOW_MINIMUM = 'PRICE_BELOW_MINIMUM',
	PRICE_BELOW_COST = 'PRICE_BELOW_COST',
	STATE_BOARD_LICENSE = 'STATE_BOARD_LICENSE',
	DEA_LICENSE = 'DEA_LICENSE',
	// PD-14226: the order landed in Approval Required because the tenant's
	// default order-submission status (instance setting) is set to Approval
	// Required — NOT because a submission check tripped. Lets the approval
	// queue show why an otherwise-clean order is held.
	DEFAULT_SETTING = 'DEFAULT_SETTING',
}
